<template>
  <div class="base-select" :class="[wrapperClass, { 'has-error': errorMessage, 'disabled': disabled, 'has-value': !!modelValue.length }]">
    <div class="base-select-field-wrapper grid grid-cols-1 grid-rows-1 items-center justify-start">
      <label
        v-if="label"
        :for="id"
        :class="{ 'sr-only': hideLabel }"
      >
        <span v-text="labelText" />
      </label>
      <select
        :id="id"
        :name="name"
        :required="required"
        :disabled="disabled"
        v-bind="$attrs"
        :class="{ 'pt-0': hideLabel }"
      >
        <option v-if="placeholderText" value="" disabled>
          {{ placeholderText }}
        </option>
        <template v-if="options.length">
          <option v-for="option in options" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </template>
        <slot v-else />
      </select>
      <div class="base-select-slot justify-self-end">
        <ChevronDownIcon class="size-[1em]" aria-hidden="true" />
      </div>
    </div>
    <div
      v-if="hasValidator"
      :title="errorMessage"
      class="input-error-msg"
    >
      <span v-text="errorMessage" />
    </div>
  </div>
</template>

<script>
import { ChevronDownIcon } from '@heroicons/vue/24/outline'

export default {
  name: 'BaseSelect',
  components: { ChevronDownIcon },
  inheritAttrs: false,
  model: {
    prop: 'modelValue',
    event: 'update:modelValue'
  },
  props: {
    type: {
      type: String,
      required: false,
      default: 'text'
    },
    name: {
      type: String,
      required: true
    },
    label: {
      type: String,
      required: false,
      default: ''
    },
    hideLabel: {
      type: Boolean,
      required: false,
      default: false
    },
    placeholder: {
      type: String,
      required: false,
      default: ''
    },
    options: {
      type: Array,
      required: false,
      default: () => []
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    wrapperClass: {
      type: String,
      required: false,
      default: ''
    },
    required: {
      type: Boolean,
      required: false,
      default: false
    },
    modelValue: {
      type: [String, Number],
      default: ''
    },
    validator: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  computed: {
    hasValidator() {
      return !!Object.keys(this.validator).length
    },
    errorMessage() {
      return this.hasValidator && this.validator.$errors?.length ? this.validator.$errors[0].$message : ''
    },
    labelText() {
      return this.label + ((this.required) ? ' *' : '')
    },
    placeholderText() {
      return this.hideLabel ? this.placeholder : ''
    },
    hasLeftSlot() {
      return !!this.$slots.left
    },
    hasRightSlot() {
      return !!this.$slots.right
    },
    id() {
      return this.$attrs.id || this.name
    }
  }
}
</script>

<style lang="scss">
.base-select {
  @apply relative;

  .base-select-field-wrapper {
    @apply relative z-1 border bg-surface h-12 rounded overflow-hidden;
    > * {
      @apply col-start-1 row-start-1;
    }
  }

  label {
    @apply antialiased block select-none whitespace-nowrap leading-none tracking-wide pointer-events-none z-10;
    padding: 0 40px 0 1em;
    transition: all 150ms;
    > span {
      transition: all 150ms;
      @apply leading-loose;
    }
  }
  select {
    @apply size-full cursor-pointer px-0 leading-snug focus:outline-none text-default bg-transparent relative appearance-none;
    padding: 10px 3em 0 1em;
  }
  &:not(.has-value) select {
    /* @apply opacity-50; */
  }

  .base-select-slot {
    @apply col-start-1 row-start-1 pointer-events-none z-10;
    max-width: 1.5em;
    margin: 0 1em;
  }

  &.disabled {
    @apply bg-grey-50 saturate-0 brightness-[.98];
    > .base-select-field-wrapper {
      @apply opacity-75;
    }
  }

  &:focus-within .base-select-field-wrapper {
    @apply border-grey-950 ring-1 ring-grey-950;
  }
  &.has-value, &:focus-within {
    label {
      @apply text-red-800 -translate-y-[.7em];
      > span {
        font-size: clamp(10px, 0.7em, 12px);
      }
    }
  }

  &.has-error:not(:focus-within) {
    .base-select-field-wrapper {
      border-bottom-color: theme('colors.red.DEFAULT');
    }
    .input-error-msg {
      @apply opacity-100 translate-x-0;
    }
  }
  .input-error-msg {
    @apply opacity-0 pointer-events-none transition-[transform,opacity] transform duration-200 -translate-x-2 origin-top grid place-content-end absolute bg-surface right-px bottom-px z-30 whitespace-nowrap overflow-ellipsis overflow-hidden text-right text-red tracking-wide py-0 px-[1em];
    > span {
      @apply text-xs;
    }
  }
}
</style>
